// Shared shell for the public corporate decks (/out-licensor-deck,
// /in-licensor-deck). Carousel + keyboard nav + `?print=1` stacked
// render used by the backend Playwright PDF routes.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ArrowRight, CheckCircle2, Download, Loader2 } from 'lucide-react';

export function DeckShell({ slides, pdfPath, pdfFilename, docTitle, testId }) {
  const [idx, setIdx] = useState(0);
  const total = slides.length;
  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);
  const containerRef = useRef(null);
  const isPrintMode = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('print') === '1';

  const [downloading, setDownloading] = useState(false);
  const handleDownloadPdf = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${pdfPath}`, { responseType: 'blob', timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('PDF download failed', e);
      const isTimeout = e?.code === 'ECONNABORTED' || (e?.message || '').toLowerCase().includes('timeout');
      alert(isTimeout
        ? 'PDF is still rendering server-side. Please try again in ~30 seconds — the second attempt is always fast.'
        : `PDF download failed: ${e?.response?.data?.detail || e?.message || 'unknown error'}. Please try again in a moment.`);
    } finally {
      setDownloading(false);
    }
  }, [pdfPath, pdfFilename]);

  useEffect(() => {
    if (isPrintMode) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') next();
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, isPrintMode]);

  useEffect(() => { document.title = docTitle; }, [docTitle]);

  if (isPrintMode) {
    return (
      <div className="bg-[#0A1128] text-white" data-testid={`${testId}-print`}>
        <style>{PRINT_CSS}</style>
        {slides.map((s, i) => (
          <section key={i} className="print-slide" data-testid={`print-slide-${i + 1}`}>
            <div className="print-inner max-w-6xl w-full"><SlideBody slide={s} /></div>
          </section>
        ))}
      </div>
    );
  }

  const slide = slides[idx];
  return (
    <div className="min-h-screen bg-[#0A1128] text-white flex flex-col" data-testid={testId}>
      <button onClick={handleDownloadPdf} disabled={downloading}
        className="fixed top-4 right-4 z-30 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#0A1128] bg-[#70C24C] hover:bg-[#8AD26A] rounded shadow-lg disabled:opacity-70 disabled:cursor-wait transition-colors"
        data-testid={`${testId}-download-pdf`} title={`Download the full ${total}-slide deck as a PDF`}>
        {downloading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing PDF…</> : <><Download className="w-3.5 h-3.5" /> Download PDF</>}
      </button>

      <div ref={containerRef} className="flex-1 flex items-center justify-center px-6 lg:px-16 py-12 lg:py-20">
        <div className="max-w-6xl w-full" data-testid={`slide-${idx + 1}`} key={idx}><SlideBody slide={slide} /></div>
      </div>

      <div className="border-t border-white/10 bg-[#070D1F]/80 backdrop-blur px-6 lg:px-16 py-3 flex items-center justify-between text-xs">
        <Link to="/" className="text-white/50 hover:text-white transition-colors">← foresightrx.com</Link>
        <div className="flex items-center gap-3">
          <button onClick={prev} disabled={idx === 0} data-testid="deck-prev"
            className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" aria-label="Previous slide">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-white/60 font-mono tabular-nums" data-testid="deck-counter">{idx + 1} / {total}</span>
          <button onClick={next} disabled={idx === total - 1} data-testid="deck-next"
            className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" aria-label="Next slide">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <span className="text-white/40">Use ← → arrow keys</span>
      </div>
    </div>
  );
}

const PRINT_CSS = `
  @page { size: 13.333333in 7.5in; margin: 0; }
  html, body { background: #0A1128 !important; margin: 0; padding: 0; }
  .print-slide {
    width: 13.333333in; height: 7.5in; position: relative;
    display: flex; align-items: center; justify-content: center;
    padding: 0.45in 0.8in; box-sizing: border-box; overflow: hidden;
    page-break-after: always; break-after: page;
    page-break-inside: avoid; break-inside: avoid;
  }
  .print-slide:last-child { page-break-after: auto; break-after: auto; }
  .print-slide .bg-clip-text, .print-slide .text-transparent {
    background: none !important; background-image: none !important;
    -webkit-background-clip: initial !important; background-clip: initial !important;
    -webkit-text-fill-color: #70C24C !important; color: #70C24C !important;
  }
  .print-slide, .print-slide * { animation: none !important; transition: none !important; }
  .print-slide h1, .print-slide h2, .print-slide p, .print-slide li, .print-slide span {
    overflow: visible; white-space: normal; word-break: normal;
  }
  .print-inner { zoom: 0.86; }
`;

function SlideBody({ slide }) {
  switch (slide.kind) {
    case 'cover': return <CoverSlide slide={slide} />;
    case 'two-col': return <TwoColSlide slide={slide} />;
    case 'process': return <ProcessSlide slide={slide} />;
    case 'regional': return <RegionalSlide slide={slide} />;
    case 'deliverables': return <DeliverablesSlide slide={slide} />;
    case 'trust': return <TrustSlide slide={slide} />;
    case 'engagement': return <EngagementSlide slide={slide} />;
    case 'stats': return <StatsSlide slide={slide} />;
    case 'options': return <OptionsSlide slide={slide} />;
    case 'cta': return <CtaSlide slide={slide} />;
    default: return null;
  }
}

// ──────────── Slide layouts ────────────
function Eyebrow({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.25em] text-[#70C24C] font-semibold mb-4">{children}</div>;
}
function H1({ children }) {
  return <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.1] tracking-tight mb-5">{children}</h1>;
}
function Sub({ children }) {
  return <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl">{children}</p>;
}
function Closer({ children }) {
  return children ? <p className="mt-6 text-sm sm:text-base text-white/85 italic border-l-2 border-[#70C24C] pl-4">{children}</p> : null;
}

function CoverSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#70C24C] to-[#00709B]">{slide.title}</span></H1>
      <Sub>{slide.sub}</Sub>
      <div className="mt-12 text-[11px] uppercase tracking-widest text-white/40">{slide.foot}</div>
    </div>
  );
}

function TwoColSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      <div className="grid md:grid-cols-2 gap-6 lg:gap-10 mt-8">
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="text-sm font-bold text-white/90 mb-4">{slide.leftTitle}</div>
          <ul className="space-y-3 text-white/70 text-sm">
            {slide.leftBullets.map((b, i) => <li key={i} className="italic">{b}</li>)}
          </ul>
        </div>
        <div className="bg-gradient-to-br from-[#70C24C]/10 to-[#00709B]/5 border border-[#70C24C]/30 rounded-xl p-6">
          <div className="text-sm font-bold text-white mb-4">{slide.rightTitle}</div>
          <ul className="space-y-3 text-white/85 text-sm">
            {slide.rightBullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#70C24C] shrink-0 mt-0.5" />{b}</li>
            ))}
          </ul>
        </div>
      </div>
      {slide.closer && <p className="mt-8 text-base sm:text-lg text-white/80 italic border-l-2 border-[#70C24C] pl-4">{slide.closer}</p>}
    </div>
  );
}

function ProcessSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      <Sub>{slide.sub}</Sub>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {slide.steps.map((s) => (
          <div key={s.n} className="relative bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-[#70C24C] text-[#0A1128] flex items-center justify-center font-black text-base">{s.n}</div>
            <div className="text-sm font-bold text-white mb-2 pt-1">{s.title}</div>
            <div className="text-xs text-white/65 leading-relaxed">{s.desc}</div>
          </div>
        ))}
      </div>
      <Closer>{slide.closer}</Closer>
    </div>
  );
}

function RegionalSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      <Sub>{slide.sub}</Sub>
      <div className="mt-8 grid md:grid-cols-2 gap-4 lg:gap-6">
        {[slide.left, slide.right].map((col, i) => (
          <div key={i} className={`rounded-xl p-6 border ${i === 0 ? 'bg-[#00709B]/10 border-[#00709B]/30' : 'bg-[#70C24C]/10 border-[#70C24C]/30'}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${i === 0 ? 'bg-[#00709B]/25 text-[#7FD0F0]' : 'bg-[#70C24C]/25 text-[#A8E082]'}`}>{col.icon}</div>
            <div className="text-base font-bold text-white mb-3">{col.title}</div>
            <ul className="space-y-2.5 text-sm text-white/80 leading-relaxed">
              {col.bullets.map((b, j) => <li key={j} className="flex gap-2"><span className="text-white/40 shrink-0">·</span><span>{b}</span></li>)}
            </ul>
          </div>
        ))}
      </div>
      <Closer>{slide.closer}</Closer>
    </div>
  );
}

function DeliverablesSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      {slide.sub && <Sub>{slide.sub}</Sub>}
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        {slide.items.map((it, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#00709B]/20 border border-[#00709B]/40 flex items-center justify-center text-[#70C24C] shrink-0">{it.icon}</div>
            <div>
              <div className="text-sm font-bold text-white mb-1">{it.title}</div>
              <div className="text-xs text-white/65 leading-relaxed">{it.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrustSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      <Sub>{slide.sub}</Sub>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {slide.pillars.map((p, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="w-9 h-9 rounded-lg bg-[#A88BFF]/15 border border-[#A88BFF]/30 flex items-center justify-center text-[#A88BFF] mb-3">{p.icon}</div>
            <div className="text-sm font-bold text-white mb-1">{p.title}</div>
            <div className="text-xs text-white/65 leading-relaxed">{p.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EngagementSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      <div className="mt-8 space-y-3">
        {slide.phases.map((p, i) => (
          <div key={i} className="flex gap-5 bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="text-[#70C24C] font-bold text-xs uppercase tracking-wider w-24 shrink-0 pt-0.5">{p.phase}</div>
            <div className="flex-1">
              <div className="text-sm font-bold text-white mb-0.5">{p.title}</div>
              <div className="text-xs text-white/65">{p.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <Closer>{slide.closer}</Closer>
    </div>
  );
}

// Big-number proof points (homepage metrics) + supporting pillars.
function StatsSlide({ slide }) {
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      <Sub>{slide.sub}</Sub>
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {slide.stats.map((s, i) => (
          <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`deck-stat-${i}`}>
            <div className="text-3xl lg:text-4xl font-black text-white tracking-tight">{s.value}</div>
            <div className="mt-1 text-xs text-white/60 font-medium">{s.label}</div>
          </div>
        ))}
      </div>
      {slide.pillars && (
        <div className="mt-6 grid md:grid-cols-3 gap-3">
          {slide.pillars.map((p, i) => (
            <div key={i} className="flex gap-3 bg-[#00709B]/10 border border-[#00709B]/30 rounded-xl p-4">
              <div className="text-[#70C24C] shrink-0 mt-0.5">{p.icon}</div>
              <div>
                <div className="text-sm font-bold text-white">{p.title}</div>
                <div className="text-xs text-white/65 leading-relaxed">{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Side-by-side commercial models (retainer / success-based / hybrid).
function OptionsSlide({ slide }) {
  const accents = [
    'bg-[#70C24C]/10 border-[#70C24C]/40',
    'bg-[#00709B]/10 border-[#00709B]/40',
    'bg-[#A88BFF]/10 border-[#A88BFF]/40',
  ];
  return (
    <div>
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1>{slide.title}</H1>
      <Sub>{slide.sub}</Sub>
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {slide.options.map((o, i) => (
          <div key={i} className={`relative rounded-xl p-5 border ${accents[i % accents.length]}`} data-testid={`deck-option-${i}`}>
            {o.tag && <div className="absolute -top-2.5 right-4 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-[#0A1128] border border-white/20 text-white/80">{o.tag}</div>}
            <div className="text-[#70C24C] mb-2">{o.icon}</div>
            <div className="text-base font-bold text-white">{o.title}</div>
            <div className="text-xs text-white/60 mt-0.5 mb-3">{o.bestFor}</div>
            <ul className="space-y-2 text-sm text-white/80">
              {o.bullets.map((b, j) => <li key={j} className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#70C24C] shrink-0 mt-0.5" />{b}</li>)}
            </ul>
          </div>
        ))}
      </div>
      <Closer>{slide.closer}</Closer>
    </div>
  );
}

function CtaSlide({ slide }) {
  return (
    <div className="text-center">
      <Eyebrow>{slide.eyebrow}</Eyebrow>
      <H1><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#70C24C] to-[#00709B]">{slide.title}</span></H1>
      <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">{slide.sub}</p>
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to={slide.primary.to}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#70C24C] hover:bg-[#5fa83e] text-[#0A1128] font-bold text-sm transition-colors"
          data-testid="deck-cta-apply">
          {slide.primary.label} <ArrowRight className="w-4 h-4" />
        </Link>
        <a href={slide.secondary.href}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-white/20 hover:bg-white/10 text-white font-medium text-sm transition-colors"
          data-testid="deck-cta-email">
          {slide.secondary.label}
        </a>
      </div>
      <div className="mt-16 text-[11px] uppercase tracking-widest text-white/40">{slide.foot}</div>
    </div>
  );
}
